import { useEffect, useState } from 'react';
import { Settings as SettingsIcon, Sparkles, Loader2, Shield, AlertCircle, CheckCircle2, LayoutGrid, Monitor, Save, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useUserStore } from '@/stores/userStore';
import { useCompactMode } from '@/hooks/useCompactMode';
import { useChatFontSize, FONT_SIZE_OPTIONS, ChatFontSize } from '@/hooks/useChatFontSize';
import { useDocumentFontSize, DOC_FONT_SIZE_OPTIONS, DocumentFontSize } from '@/hooks/useDocumentFontSize';
import { useResourcesFontSize, RESOURCES_FONT_SIZE_OPTIONS, ResourcesFontSize } from '@/hooks/useResourcesFontSize';
import { useChatLanguage, LANGUAGE_OPTIONS, ChatLanguage } from '@/hooks/useChatLanguage';
import { useChatModel, ChatModel } from '@/hooks/useChatModel';
import { twoFactorApi } from '@/services/api';

interface SettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type SettingsSection = 'all' | 'display' | 'session' | 'security' | 'onboarding';

export function SettingsModal({ open, onOpenChange }: SettingsModalProps) {
  const userSettings = useUserStore(state => state.userSettings);
  const updateUserSettings = useUserStore(state => state.updateUserSettings);
  const loadUserSettingsFromBackend = useUserStore(state => state.loadUserSettingsFromBackend);
  const [isLoading, setIsLoading] = useState(false);
  const { compactMode, setCompactMode } = useCompactMode();
  const { chatFontSize, setChatFontSize } = useChatFontSize();
  const { documentFontSize, setDocumentFontSize } = useDocumentFontSize();
  const { resourcesFontSize, setResourcesFontSize } = useResourcesFontSize();
  const { chatLanguage, setChatLanguage } = useChatLanguage();
  // The account-aware list; see ChatSettingsMenu for why the static one is wrong here.
  const { chatModel, setChatModel, options: chatModelOptions } = useChatModel();

  // Navigation state
  const [currentSection, setCurrentSection] = useState<SettingsSection>('all');

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [showTestCodeForm, setShowTestCodeForm] = useState(false);
  const [testCodeSent, setTestCodeSent] = useState(false);
  const [testCode, setTestCode] = useState('');
  const [testCodeError, setTestCodeError] = useState('');
  const [testCodeSuccess, setTestCodeSuccess] = useState(false);
  const [isDeviceTrusted, setIsDeviceTrusted] = useState(false);

  // Load settings from backend when modal opens
  useEffect(() => {
    if (open) {
      console.log('[SettingsModal] Modal opened, loading settings from backend');
      setIsLoading(true);
      loadUserSettingsFromBackend().finally(() => {
        setIsLoading(false);
      });
    }
  }, [open, loadUserSettingsFromBackend]);

  // Fetch 2FA status when modal opens
  useEffect(() => {
    const fetch2FAStatus = async () => {
      try {
        const result = await twoFactorApi.get2FAStatus();
        setTwoFactorEnabled(result.two_factor_enabled);
      } catch (error) {
        console.error('Failed to fetch 2FA status:', error);
      }
    };

    const checkDeviceTrust = () => {
      try {
        const username = localStorage.getItem('username');
        console.log('Checking device trust for username:', username);
        if (!username) {
          console.log('No username found in localStorage');
          setIsDeviceTrusted(false);
          return;
        }

        const normalizedUsername = username.toLowerCase();
        const storageKey = `device_token_${normalizedUsername}`;
        const stored = localStorage.getItem(storageKey);
        console.log('Looking for device token at key:', storageKey, 'found:', !!stored);

        if (!stored) {
          setIsDeviceTrusted(false);
          return;
        }

        const tokenData = JSON.parse(stored);
        const expiry = new Date(tokenData.expiry);
        console.log('Device token expiry:', expiry, 'current time:', new Date());

        // Check if expired
        if (new Date() > expiry) {
          console.log('Device token expired, removing');
          localStorage.removeItem(storageKey);
          setIsDeviceTrusted(false);
        } else {
          console.log('Device token valid, device is trusted');
          setIsDeviceTrusted(true);
        }
      } catch (error) {
        console.warn('Failed to check device trust:', error);
        setIsDeviceTrusted(false);
      }
    };

    if (open) {
      fetch2FAStatus();
      checkDeviceTrust();
    }
  }, [open]);

  const showWizardOnStartup = userSettings.showWizardOnStartup !== false; // Default to true if not set

  // 2FA handlers
  const handleToggle2FA = async () => {
    setTwoFactorLoading(true);
    try {
      if (twoFactorEnabled) {
        await twoFactorApi.disable2FA();
        setTwoFactorEnabled(false);
        setShowTestCodeForm(false);
      } else {
        await twoFactorApi.enable2FA();
        setTwoFactorEnabled(true);
      }
    } catch (error) {
      console.error('Failed to toggle 2FA:', error);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleSendTestCode = async () => {
    try {
      await twoFactorApi.send2FACode();
      setTestCodeSent(true);
      setShowTestCodeForm(true);
    } catch (error) {
      console.error('Failed to send test code:', error);
      setTestCodeError('Failed to send test code');
    }
  };

  const handleVerifyTestCode = async () => {
    setTestCodeError('');
    setTestCodeSuccess(false);
    try {
      const result = await twoFactorApi.verify2FACode(testCode, false);
      if (result.success) {
        setTestCodeSuccess(true);
        setTestCodeError('');
        // Clear form after a delay to show success message
        setTimeout(() => {
          setShowTestCodeForm(false);
          setTestCode('');
          setTestCodeSent(false);
          setTestCodeSuccess(false);
        }, 3000);
      }
    } catch (error: any) {
      setTestCodeError(error.message || 'Verification failed');
      setTestCodeSuccess(false);
    }
  };

  const handleUntrustDevice = () => {
    try {
      const username = localStorage.getItem('username');
      if (username) {
        const normalizedUsername = username.toLowerCase();
        localStorage.removeItem(`device_token_${normalizedUsername}`);
        setIsDeviceTrusted(false);
        console.log('Device untrusted for user:', normalizedUsername);
      }
    } catch (error) {
      console.error('Failed to untrust device:', error);
    }
  };

  const handleToggleWizardOnStartup = (checked: boolean) => {
    updateUserSettings('showWizardOnStartup', checked);
  };

  const handleShowWizardNow = () => {
    // Close the settings modal first
    onOpenChange(false);

    // Wait for modal to fully close before showing wizard
    setTimeout(() => {
      // Reset onboarding and trigger the wizard event
      updateUserSettings('hasCompletedOnboarding', false);
      window.dispatchEvent(new Event('open-onboarding-wizard'));
    }, 300); // Wait for dialog close animation
  };

  const sections = [
    { id: 'all' as SettingsSection, label: 'All Settings', icon: LayoutGrid },
    { id: 'display' as SettingsSection, label: 'Display', icon: Monitor },
    { id: 'session' as SettingsSection, label: 'Session', icon: Save },
    { id: 'security' as SettingsSection, label: 'Security', icon: Shield },
    { id: 'onboarding' as SettingsSection, label: 'Onboarding', icon: Sparkles },
  ];

  const shouldShowSection = (section: SettingsSection) => {
    return currentSection === 'all' || currentSection === section;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[700px] md:max-w-[900px] lg:max-w-[1000px] max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-gray-700" />
            <DialogTitle>Settings</DialogTitle>
          </div>
          <DialogDescription>
            Manage your Patent Loop preferences
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Side Navigation */}
          <div className="w-48 border-r border-gray-200 bg-gray-50 py-4">
            <nav className="space-y-1 px-2">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setCurrentSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                      currentSection === section.id
                        ? 'bg-primary text-white'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {section.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                <span className="ml-3 text-sm text-gray-600">Loading settings...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Onboarding Section */}
                {shouldShowSection('onboarding') && (
                  <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Onboarding</h3>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="quickstart-startup" className="text-sm font-medium">
                      Show Quick Start on startup
                    </Label>
                    <p className="text-xs text-gray-500">
                      Display the Quick Start screen each time you log in (takes effect on next login)
                    </p>
                  </div>
                  <Switch
                    id="quickstart-startup"
                    checked={showWizardOnStartup}
                    onCheckedChange={handleToggleWizardOnStartup}
                    disabled={isLoading}
                  />
                </div>

                <div className="pt-2">
                  <Button
                    onClick={handleShowWizardNow}
                    variant="outline"
                    className="w-full gap-2"
                    disabled={isLoading}
                  >
                    <Sparkles className="w-4 h-4" />
                    Show Quick Start Now
                  </Button>
                </div>
                  </div>
                )}

              {/* Display Section */}
              {shouldShowSection('display') && (
                <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Display</h3>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="compact-mode" className="text-sm font-medium">
                      Compact Mode
                    </Label>
                    <p className="text-xs text-gray-500">
                      Reduce spacing and font sizes throughout the application for a denser layout
                    </p>
                  </div>
                  <Switch
                    id="compact-mode"
                    checked={compactMode}
                    onCheckedChange={setCompactMode}
                    disabled={isLoading}
                  />
                </div>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="chat-model" className="text-sm font-medium">
                      Chat Model
                    </Label>
                    <p className="text-xs text-gray-500">
                      AI model used to generate chat responses
                    </p>
                  </div>
                  <Select
                    value={chatModel}
                    onValueChange={(value) => setChatModel(value as ChatModel)}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {chatModelOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                        >
                          {option.label}
                          {option.disabled && (
                            <span className="text-xs text-amber-700"> — on request</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="chat-language" className="text-sm font-medium">
                      Chat Response Language
                    </Label>
                    <p className="text-xs text-gray-500">
                      Language for AI chat responses
                    </p>
                  </div>
                  <Select
                    value={chatLanguage}
                    onValueChange={(value) => setChatLanguage(value as ChatLanguage)}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {LANGUAGE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}{option.nativeName ? ` (${option.nativeName})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="chat-font-size" className="text-sm font-medium">
                      Chat Font Size
                    </Label>
                    <p className="text-xs text-gray-500">
                      Adjust the font size for chat messages
                    </p>
                  </div>
                  <Select
                    value={chatFontSize}
                    onValueChange={(value) => setChatFontSize(value as ChatFontSize)}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_SIZE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <span style={{ fontSize: option.size }}>{option.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="doc-font-size" className="text-sm font-medium">
                      Document Font Size
                    </Label>
                    <p className="text-xs text-gray-500">
                      Adjust the font size for document viewer content
                    </p>
                  </div>
                  <Select
                    value={documentFontSize}
                    onValueChange={(value) => setDocumentFontSize(value as DocumentFontSize)}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOC_FONT_SIZE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <span style={{ fontSize: option.size }}>{option.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="resources-font-size" className="text-sm font-medium">
                      Resources Font Size
                    </Label>
                    <p className="text-xs text-gray-500">
                      Adjust the font size for resources page content (MPEP, CFR, USC, etc.)
                    </p>
                  </div>
                  <Select
                    value={resourcesFontSize}
                    onValueChange={(value) => setResourcesFontSize(value as ResourcesFontSize)}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOURCES_FONT_SIZE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <span style={{ fontSize: option.size }}>{option.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                </div>
              )}

              {/* Session Section */}
              {shouldShowSection('session') && (
                <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Session</h3>

                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="auto-save" className="text-sm font-medium">
                      Auto-Save Sessions
                    </Label>
                    <p className="text-xs text-gray-500">
                      Automatically save your session when you send messages or add documents
                    </p>
                  </div>
                  <Switch
                    id="auto-save"
                    checked={userSettings.autoSaveEnabled === true}
                    onCheckedChange={(checked) => updateUserSettings('autoSaveEnabled', checked)}
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between space-x-4">
                    <div className="flex-1 space-y-1">
                      <Label htmlFor="session-timeout" className="text-sm font-medium">
                        Session Timeout
                      </Label>
                      <p className="text-xs text-gray-500">
                        How long the session stays active while you're away
                      </p>
                    </div>
                    <Select
                      value={String(userSettings.sessionTimeout !== undefined && userSettings.sessionTimeout !== null ? userSettings.sessionTimeout : 30)}
                      onValueChange={(value) => updateUserSettings('sessionTimeout', Number(value))}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="w-[150px]">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-gray-500" />
                          <SelectValue placeholder="Select timeout" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                        <SelectItem value="240">4 hours</SelectItem>
                        <SelectItem value="480">8 hours</SelectItem>
                        <SelectItem value="0">Never</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(userSettings.sessionTimeout as number) === 0 && (
                    <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                      <Shield className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-800 leading-relaxed">
                        Your session will never expire automatically. For security, always log out manually when using a shared or public device.
                      </p>
                    </div>
                  )}
                </div>
                </div>
              )}

              {/* Security Section */}
              {shouldShowSection('security') && (
                <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-gray-700" />
                  <h3 className="text-sm font-semibold text-gray-900">Security</h3>
                </div>

                {/* 2FA Status */}
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="two-factor" className="text-sm font-medium">
                      Two-Factor Authentication
                    </Label>
                    <p className="text-xs text-gray-500">
                      Add an extra layer of security to your account with email verification
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={twoFactorEnabled ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}>
                        {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </div>
                  <Switch
                    id="two-factor"
                    checked={twoFactorEnabled}
                    onCheckedChange={handleToggle2FA}
                    disabled={isLoading || twoFactorLoading}
                  />
                </div>

                {/* Test 2FA Section */}
                {twoFactorEnabled && (
                  <div className="pl-4 border-l-2 border-gray-200 space-y-3">
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Test Two-Factor Authentication</Label>
                      <p className="text-xs text-gray-500">
                        Send a test code to verify 2FA is working correctly
                      </p>
                    </div>

                    {!showTestCodeForm ? (
                      <Button
                        onClick={handleSendTestCode}
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                      >
                        Send Test Code
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-xs text-green-600">
                          <CheckCircle2 className="w-3 h-3" />
                          <span>Code sent to your email</span>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs font-medium text-gray-700">
                            Enter 6-digit code
                          </Label>
                          <Input
                            type="text"
                            value={testCode}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                              setTestCode(value);
                            }}
                            placeholder="000000"
                            maxLength={6}
                            className="font-mono text-center text-sm tracking-widest"
                          />
                        </div>

                        {testCodeSuccess && (
                          <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 p-2 rounded">
                            <CheckCircle2 className="w-3 h-3" />
                            <span className="font-medium">Test successful! 2FA is working correctly.</span>
                          </div>
                        )}

                        {testCodeError && !testCodeSuccess && (
                          <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                            <AlertCircle className="w-3 h-3" />
                            <span>{testCodeError}</span>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button
                            onClick={handleVerifyTestCode}
                            disabled={testCode.length !== 6}
                            size="sm"
                          >
                            Verify
                          </Button>
                          <Button
                            onClick={() => {
                              setShowTestCodeForm(false);
                              setTestCode('');
                              setTestCodeSent(false);
                              setTestCodeError('');
                              setTestCodeSuccess(false);
                            }}
                            variant="outline"
                            size="sm"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-gray-500 space-y-1 pt-2">
                      <p>• Codes expire after 15 minutes</p>
                      <p>• Trust devices for 30 days</p>
                      <p>• Maximum 3 attempts per code</p>
                    </div>
                  </div>
                )}

                {/* Device Trust Section */}
                {twoFactorEnabled && (
                  <div className="pl-4 border-l-2 border-gray-200 space-y-3">
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Trusted Device</Label>
                      <p className="text-xs text-gray-500">
                        Manage trusted device status for this browser
                      </p>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {isDeviceTrusted ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-gray-700">This device is trusted</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-500">This device is not trusted</span>
                          </>
                        )}
                      </div>
                      {isDeviceTrusted && (
                        <Button
                          onClick={handleUntrustDevice}
                          variant="outline"
                          size="sm"
                        >
                          Untrust Device
                        </Button>
                      )}
                    </div>

                    {!isDeviceTrusted && (
                      <p className="text-xs text-gray-500">
                        Check "Trust this device" during login to avoid entering codes for 30 days
                      </p>
                    )}
                  </div>
                )}
                </div>
              )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
